<template>
    <Transition
        enter-active-class="tw-transition-all tw-duration-200 tw-ease-out"
        enter-from-class="tw-opacity-0 tw-translate-y-2"
        enter-to-class="tw-opacity-100 tw-translate-y-0"
        leave-active-class="tw-transition-all tw-duration-150 tw-ease-in"
        leave-from-class="tw-opacity-100 tw-translate-y-0"
        leave-to-class="tw-opacity-0 tw-translate-y-2"
    >
        <div v-if="visible" class="tw-fixed tw-inset-x-3 tw-z-[55] tw-bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] lg:tw-bottom-6 lg:tw-inset-x-auto lg:tw-right-6 lg:tw-max-w-sm">
            <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-2xl tw-shadow-[0_15px_40px_rgba(0,0,0,0.18)] tw-p-4">
                <div class="tw-flex tw-items-start tw-gap-3">
                    <div class="tw-flex-shrink-0 tw-mt-0.5 tw-h-9 tw-w-9 tw-rounded-full tw-flex tw-items-center tw-justify-center" :class="phase === 'error' ? 'tw-bg-red-500/10' : 'tw-bg-emerald-500/10'">
                        <mdicon :name="headerIcon" size="20" :class="[phase === 'error' ? 'tw-text-red-500' : 'tw-text-emerald-600', phase === 'downloading' || phase === 'applying' ? 'tw-animate-spin' : '']" />
                    </div>
                    <div class="tw-min-w-0 tw-flex-1">
                        <p class="tw-text-sm tw-font-semibold tw-text-gray-900">{{ title }}</p>
                        <p class="tw-mt-0.5 tw-text-xs tw-text-gray-600">{{ subtitle }}</p>
                        <p v-if="phase === 'ready'" class="tw-mt-1.5 tw-text-xs tw-text-gray-500 tw-italic">Met verbeteringen en kleine correcties voor een soepelere ervaring.</p>
                    </div>
                </div>

                <!-- Progress-indicator: determinate tijdens download (met %), of een
                     doorlopende indeterminate balk tijdens installeren. -->
                <div v-if="phase === 'downloading' || phase === 'applying'" class="tw-mt-3">
                    <div class="tw-h-1.5 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-gray-100">
                        <div v-if="phase === 'downloading' && state.downloadProgress >= 0" class="tw-h-full tw-rounded-full tw-bg-emerald-500 tw-transition-[width] tw-duration-300 tw-ease-out" :style="{ width: state.downloadProgress + '%' }" />
                        <div v-else class="tw-h-full tw-w-1/3 tw-rounded-full tw-bg-emerald-500 lu-indeterminate" />
                    </div>
                    <p v-if="phase === 'downloading' && state.downloadProgress >= 0" class="tw-mt-1 tw-text-right tw-text-[11px] tw-tabular-nums tw-text-gray-500">{{ state.downloadProgress }}%</p>
                </div>

                <!-- Acties -->
                <div class="tw-mt-3 tw-flex tw-items-center tw-justify-end tw-gap-2">
                    <button v-if="showLater" type="button" class="tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-gray-700 tw-bg-gray-100 hover:tw-bg-gray-200 tw-border tw-border-gray-200 tw-rounded-lg" @click="onDismiss">Later</button>

                    <button v-if="phase === 'error'" type="button" class="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-bg-emerald-600 hover:tw-bg-emerald-700 tw-rounded-lg" @click="onRetry">
                        <mdicon name="refresh" size="16" />
                        Opnieuw proberen
                    </button>

                    <button v-else type="button" :disabled="!state.ready || state.applying" class="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-bg-emerald-600 hover:tw-bg-emerald-700 tw-rounded-lg disabled:tw-opacity-60 disabled:tw-cursor-not-allowed" @click="onApply">
                        <mdicon v-if="state.applying || phase === 'downloading'" name="loading" size="16" class="tw-animate-spin" />
                        {{ primaryLabel }}
                    </button>
                </div>
            </div>
        </div>
    </Transition>
</template>

<script>
import { liveUpdateState, isPromptVisible, dismiss, applyNow, retryDownload } from "@/services/liveUpdate";

export default {
    name: "LiveUpdatePrompt",
    data() {
        return {
            state: liveUpdateState,
        };
    },
    computed: {
        visible() {
            // isPromptVisible leest de reactieve service-state; door de velden
            // hier óók aan te raken blijft deze computed reactief.
            void this.state.available;
            void this.state.downloading;
            void this.state.ready;
            void this.state.error;
            void this.state.mandatory;
            void this.state.dismissedUntil;
            return isPromptVisible();
        },
        // Eén afgeleide fase stuurt de hele kaart aan.
        phase() {
            if (this.state.applying) return "applying";
            if (this.state.error && !this.state.downloading) return "error";
            if (this.state.downloading || !this.state.ready) return "downloading";
            return "ready";
        },
        headerIcon() {
            switch (this.phase) {
                case "error":
                    return "alert-circle-outline";
                case "applying":
                case "downloading":
                    return "loading";
                default:
                    return "download";
            }
        },
        title() {
            switch (this.phase) {
                case "error":
                    return "Update mislukt";
                case "applying":
                    return "Bezig met installeren…";
                case "downloading":
                    return "Update downloaden…";
                default:
                    return this.state.mandatory ? "Update vereist" : "Update klaar";
            }
        },
        subtitle() {
            switch (this.phase) {
                case "error":
                    return this.state.error || "Er ging iets mis bij het downloaden.";
                case "applying":
                    return "De app herstart zo met de nieuwe versie.";
                case "downloading":
                    return "Een nieuwere versie van NovaCheck wordt opgehaald.";
                default:
                    return this.state.mandatory ? "Er staat al een tijdje een update klaar. Tik om bij te werken." : "De nieuwe versie staat klaar. Bijwerken duurt een paar seconden.";
            }
        },
        primaryLabel() {
            if (this.state.applying) return "Bijwerken…";
            if (this.phase === "downloading") return "Voorbereiden…";
            return "Bijwerken";
        },
        // "Later" mag bij een niet-verplichte update zolang we niet aan het
        // installeren zijn (de reload is dan al onderweg).
        showLater() {
            return !this.state.mandatory && !this.state.applying;
        },
    },
    methods: {
        onDismiss() {
            dismiss();
        },
        onApply() {
            applyNow();
        },
        onRetry() {
            retryDownload();
        },
    },
};
</script>

<style scoped>
/* Doorlopende indeterminate-balk voor de installatiefase. */
@keyframes lu-indeterminate {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(300%);
    }
}
.lu-indeterminate {
    animation: lu-indeterminate 1.1s ease-in-out infinite;
}
</style>
